import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useFrustrationSignalConfigs } from '@src/hooks/frustrationSignal/useFrustrationSignalConfigs';
import { Input, Space, Divider } from 'antd';
import { selectedFrustrationSignalsAtom } from '../../atoms';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  StyledBody,
  SignalBox,
  SignalContent,
  SignalName,
  SectionHeader,
  EmptyState,
  SignalMainPanel,
  DrawerCardWrapper,
} from './styled';

const DrawerCard = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const [selectedSignalIds, setSelectedSignalIds] = useAtom(selectedFrustrationSignalsAtom);
  const signalConfigs = useFrustrationSignalConfigs();

  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<any>(null);

  // Focus search input when drawer opens
  useEffect(() => {
    if (isDrawerOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDrawerOpen]);

  // Apply search filter and sort alphabetically
  const filteredSignals = useMemo(() => {
    const signals =
      !signalConfigs || !searchTerm
        ? Object.values(signalConfigs || {})
        : Object.values(signalConfigs).filter(
            (signal) =>
              signal.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              signal.identifier?.toLowerCase().includes(searchTerm.toLowerCase()),
          );

    return signals.sort((a, b) => a.name.localeCompare(b.name));
  }, [signalConfigs, searchTerm]);

  const toggleSelect = (id: string) => {
    const idStr = id.toString();
    const updated = selectedSignalIds.includes(idStr)
      ? selectedSignalIds.filter((s) => s !== idStr)
      : [...selectedSignalIds, idStr];

    setSelectedSignalIds(updated);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  if (!signalConfigs || Object.keys(signalConfigs).length === 0) {
    return (
      <div>
        <div>No frustration signals available.</div>
      </div>
    );
  }

  return (
    <DrawerCardWrapper>
      <Space direction="vertical" size="middle" style={{ flexShrink: 0 }}>
        <Input
          ref={searchInputRef}
          placeholder="Search frustration signals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            searchTerm ? (
              <CloseCircleOutlined
                onClick={handleClearSearch}
                style={{ cursor: 'pointer', color: '#999' }}
              />
            ) : null
          }
          allowClear={false}
        />
      </Space>

      <Divider size="small" style={{ flexShrink: 0 }} />

      {filteredSignals.length === 0 ? (
        <EmptyState>No frustration signals found matching your criteria.</EmptyState>
      ) : (
        <SignalMainPanel>
          {filteredSignals.map((item) => {
            const isSelected = selectedSignalIds.includes(item.id.toString());
            return (
              <SignalBox
                onClick={() => toggleSelect(item.id)}
                $isSelected={isSelected}
                key={item.id}
              >
                <StyledBody>
                  <SignalContent>
                    <SignalName>{item.name}</SignalName>
                  </SignalContent>
                </StyledBody>
              </SignalBox>
            );
          })}
        </SignalMainPanel>
      )}
    </DrawerCardWrapper>
  );
};

export default DrawerCard;
